import {Component, OnInit} from '@angular/core';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {ConfigurationService} from '../../service/configuration.service';
import {UntilDestroy} from '@ngneat/until-destroy';
import {tap, map} from 'rxjs/operators';
import {PortalHotToastService, getUser, httpOptions} from '@portal-workspace/grow-ui-library';
import {setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {Subscription} from 'rxjs';
import { SlideToggleValue, FeatureFlag, UserOmitPriviledges, PayloadApiResponse } from '@portal-workspace/grow-shared-library';
import { StatusToggleComponent } from '@portal-workspace/grow-ui-library';
import { MatCardModule } from '@angular/material/card';
import { FeatureFlagService } from '../../service/feature-flag.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpClient } from '@angular/common/http';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { environment } from '../../../environments/environment';


@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    templateUrl: './set-maintenance.page.html',
    styleUrls: ['./set-maintenance.page.scss'],
    standalone: true,
    imports: [
      CommonModule,
      MatCardModule, 
      StatusToggleComponent, 
      FormsModule, 
      ReactiveFormsModule,
      MatSlideToggleModule,
      MatDividerModule,
      MatProgressSpinnerModule,
      MatChipsModule,
      MatIconModule,
      MatButtonModule,
      MatDialogModule,
      MatSelectModule,
      MatFormFieldModule,
      MatOptionModule
    ]
})
export class SetMaintenancePage implements OnInit {

  subscriptions: Subscription[] = [];

  formControlMaintenanceMode: FormControl<SlideToggleValue>;
  featureFlags: FeatureFlag[] = [];
  isLoading = true;
  allUsers: UserOmitPriviledges[] = [];
  expandedFlags: Set<string> = new Set();
  selectedUsersForFlag: { [featureName: string]: number[] } = {};

  constructor(private formBuilder: FormBuilder,
              private configurationService: ConfigurationService,
              private featureFlagService: FeatureFlagService,
              private toastService: PortalHotToastService,
              private httpClient: HttpClient) {
    this.formControlMaintenanceMode = this.formBuilder.control(null);
  }


  ngOnInit(): void {
    setupUntilDestroy(this);
    this.loadAllData();
    this.loadAllUsers();

    // Setup maintenance mode form listener
    const formChangeSub = this.formControlMaintenanceMode.valueChanges.subscribe(changes => {
        this.onMaintenanceChange(changes);
      }
    );
    this.subscriptions.push(formChangeSub);
  }

  loadAllData(): void {
    this.isLoading = true;
    
    // Load both configuration and feature flags
    const sub = this.configurationService.getConfigurations().pipe(
      tap(r => {
        const m = r.payload.MaintenanceMode;
        m === 'enable' ? this.formControlMaintenanceMode.setValue(true) : this.formControlMaintenanceMode.setValue(false);
      })
    ).subscribe({
      next: () => {
        // After maintenance mode is loaded, load feature flags
        this.loadFeatureFlags();
      },
      error: (error) => {
        console.error('Error loading configuration:', error);
        this.toastService.error('Failed to load configuration');
        this.isLoading = false;
      }
    });
    this.subscriptions.push(sub);
  }

  onMaintenanceChange($event: boolean | null) {
    const b = $event ? 'enable' : 'disable';
    this.configurationService.updateConfiguration({
      MaintenanceMode: b,
    }).pipe(
    ).subscribe();
  }

  loadFeatureFlags(): void {
    this.subscriptions.push(
      this.featureFlagService.getAllFeatureFlags().subscribe({
        next: (flags: FeatureFlag[]) => {
          this.featureFlags = flags;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading feature flags:', error);
          this.toastService.error('Failed to load feature flags');
          this.isLoading = false;
        }
      })
    );
  }

  onFeatureFlagToggleChange(flag: FeatureFlag): void {
    const user = getUser();
    const userId = user?.UserId;

    this.subscriptions.push(
      this.featureFlagService.updateFeatureFlag({
        featureName: flag.featureName,
        flagValue: flag.flagValue,
        updatedBy: userId
      }).pipe(
        this.toastService.spinnerObservable()
      ).subscribe({
        next: (updatedFlag: FeatureFlag) => {
          this.toastService.success(`Feature flag '${this.getFlagLabel(updatedFlag.featureName)}' updated successfully`);
          // Update the local flag with the response
          const index = this.featureFlags.findIndex(f => f.featureName === updatedFlag.featureName);
          if (index !== -1) {
            this.featureFlags[index] = updatedFlag;
          }
        },
        error: (error) => {
          console.error('Error updating feature flag:', error);
          this.toastService.error('Failed to update feature flag');
          // Revert the toggle
          flag.flagValue = !flag.flagValue;
        }
      })
    );
  }

  getFlagLabel(featureName: string): string {
    return featureName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  getFlagValueLabel(flag: FeatureFlag): string {
    return flag.flagValue ? 'Enabled' : 'Disabled';
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '';
    
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else {
      return d.toLocaleDateString('en-AU', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }

  loadAllUsers(): void {
    this.subscriptions.push(
      this.httpClient.get<PayloadApiResponse<UserOmitPriviledges[]>>(`${environment.api2Host}/api2/users/all`, httpOptions())
        .pipe(map((res: PayloadApiResponse<UserOmitPriviledges[]>) => res.payload))
        .subscribe({
          next: (users: UserOmitPriviledges[]) => {
            this.allUsers = users.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
          },
          error: (error) => {
            console.error('Error loading users:', error);
            this.toastService.error('Failed to load users');
          }
        })
    );
  }

  toggleUserAccessExpansion(flag: FeatureFlag): void {
    if (this.expandedFlags.has(flag.featureName)) {
      this.expandedFlags.delete(flag.featureName);
    } else {
      this.expandedFlags.add(flag.featureName);
      // Initialize selected users for this flag
      if (!this.selectedUsersForFlag[flag.featureName]) {
        this.selectedUsersForFlag[flag.featureName] = flag.allowedUserIds || [];
      }
    }
  }

  isUserAccessExpanded(flag: FeatureFlag): boolean {
    return this.expandedFlags.has(flag.featureName);
  }

  getUserName(userId: number): string {
    const user = this.allUsers.find(u => u.UserId === userId);
    return user?.Name || `User ${userId}`;
  }

  getUserEmail(userId: number): string | undefined {
    const user = this.allUsers.find(u => u.UserId === userId);
    return user?.Email;
  }

  removeUserFromFlag(flag: FeatureFlag, userId: number): void {
    if (!this.selectedUsersForFlag[flag.featureName]) {
      this.selectedUsersForFlag[flag.featureName] = flag.allowedUserIds || [];
    }
    this.selectedUsersForFlag[flag.featureName] = this.selectedUsersForFlag[flag.featureName].filter(id => id !== userId);
    this.saveUserAccess(flag);
  }

  addUserToFlag(flag: FeatureFlag, userId: number): void {
    if (!this.selectedUsersForFlag[flag.featureName]) {
      this.selectedUsersForFlag[flag.featureName] = flag.allowedUserIds || [];
    }
    if (!this.selectedUsersForFlag[flag.featureName].includes(userId)) {
      this.selectedUsersForFlag[flag.featureName].push(userId);
      this.saveUserAccess(flag);
    }
  }

  saveUserAccess(flag: FeatureFlag): void {
    const user = getUser();
    const userId = user?.UserId;

    this.subscriptions.push(
      this.featureFlagService.updateFeatureFlagUserAccess({
        featureName: flag.featureName,
        userIds: this.selectedUsersForFlag[flag.featureName] || [],
        updatedBy: userId
      }).pipe(
        this.toastService.spinnerObservable()
      ).subscribe({
        next: (updatedFlag: FeatureFlag) => {
          this.toastService.success(`User access for '${this.getFlagLabel(updatedFlag.featureName)}' updated successfully`);
          // Update the local flag with the response
          const index = this.featureFlags.findIndex(f => f.featureName === updatedFlag.featureName);
          if (index !== -1) {
            this.featureFlags[index] = updatedFlag;
            this.selectedUsersForFlag[flag.featureName] = updatedFlag.allowedUserIds || [];
          }
        },
        error: (error) => {
          console.error('Error updating feature flag user access:', error);
          this.toastService.error('Failed to update user access');
        }
      })
    );
  }

  getAvailableUsersForFlag(flag: FeatureFlag): UserOmitPriviledges[] {
    const selectedIds = this.selectedUsersForFlag[flag.featureName] || flag.allowedUserIds || [];
    return this.allUsers.filter(user => !selectedIds.includes(user.UserId));
  }

  hasUserAccessRestrictions(flag: FeatureFlag): boolean {
    return (flag.allowedUserIds && flag.allowedUserIds.length > 0) || 
           (this.selectedUsersForFlag[flag.featureName] && this.selectedUsersForFlag[flag.featureName].length > 0);
  }
}
